/**
 * WordPress dependencies
 */
import { __, sprintf } from '@wordpress/i18n';

/**
 * Internal dependencies
 */
import { HOTSPOT_CONSTANTS } from '../../utils/constants';
import { getLayerDisplayName } from '../../utils/layerActions.js';
import { resolveHotspotStyle, isButtonHotspot } from '../../utils/hotspotStyle';
import { resolveButtonCtaStyle, getButtonCtaClassName, getButtonCtaCssVars, getButtonFontPx, resolveButtonFontPercent, fitButtonFontPx } from '../../utils/buttonCtaStyle';

// Every rendered hotspot point: circles (pulse / icon style) and buttons (button
// style). Points are matched to `layer.hotspots` by index, so both must be found.
const HOTSPOT_POINT_SELECTOR = '.hotspot, .godam-hotspot-button';

/**
 * Whether an authored link is safe to make navigable. Only http(s) destinations
 * qualify (relative URLs resolve against the page), never `javascript:`, `data:`
 * and friends — `noopener` and `textContent` don't validate the destination, and
 * the editor's own URL check only shows an error, it doesn't stop the value being
 * saved.
 *
 * @param {string} url Authored link.
 * @return {boolean} True when the link resolves to an http(s) URL.
 */
function isSafeLinkUrl( url ) {
	try {
		const { protocol } = new URL( url, window.location.href );
		return protocol === 'http:' || protocol === 'https:';
	} catch ( e ) {
		return false;
	}
}

/**
 * Resolve the analytics videoKey (data-id or data-job_id) for a player.
 *
 * @param {Object} player VideoJS player instance.
 * @return {string} Non-empty videoKey on success; empty string when no usable identifier is present.
 */
function getVideoKey( player ) {
	try {
		const el = player?.el && player.el();
		if ( ! el ) {
			return '';
		}
		const id = el.getAttribute?.( 'data-id' ) || el.dataset?.id;
		if ( id ) {
			return String( id );
		}
		const jobId = el.getAttribute?.( 'data-job_id' ) || el.dataset?.job_id;
		return jobId ? String( jobId ) : '';
	} catch ( e ) {
		return '';
	}
}

/**
 * Hotspot Layer Manager
 * Handles hotspot layer functionality including creation, positioning, and interaction
 */
export default class HotspotLayerManager {
	static BASE_WIDTH = HOTSPOT_CONSTANTS.BASE_WIDTH;
	static BASE_HEIGHT = HOTSPOT_CONSTANTS.BASE_HEIGHT;

	constructor( player, isDisplayingLayers, currentPlayerVideoInstanceId ) {
		this.player = player;
		this.hotspotLayers = [];
		this.wasPlayingBeforeHover = false;
		this.isDisplayingLayers = isDisplayingLayers;
		this.currentPlayerVideoInstanceId = currentPlayerVideoInstanceId;

		// Per-(composite layer_id, action_type, page-session) dedupe map.
		// Composite layer_id is `${parentLayer.id}::${hotspot.id || `idx${index}`}`
		// so each sub-hotspot is its own atomic unit in the analytics.
		// Map<compositeLayerId, Set<actionType>>.
		this._dedupeFired = new Map();

		// First-visible timestamp keyed by composite layer_id (epoch ms).
		// Used to compute dwell_ms on click/hover. Map<compositeLayerId, number>.
		this._firstVisibleAt = new Map();

		// Snapshot of `window.GoDAM.getTabHiddenAccumulatedMs()` at the moment
		// `_firstVisibleAt` is set — subtracted from wall-clock dwell so tab-
		// away time doesn't inflate "consideration time" reporting.
		// Map<compositeLayerId | parentLayerId, number>.
		this._hiddenAtFirstVisible = new Map();

		// Interaction sequence keyed by composite layer_id. Map<compositeLayerId, number>.
		this._interactionSeq = new Map();
	}

	/**
	 * Build a stable composite layer_id for a sub-hotspot.
	 *
	 * Prefers the hotspot's own `id` field (a UUID set by the editor) so
	 * reordering or removing hotspots doesn't shift the analytics key.
	 * Falls back to `idx<index>` only when no stable id is available — in
	 * which case reordering will misattribute, documented as a known limit.
	 *
	 * @param {Object} parentLayer Parent layer config with an .id field.
	 * @param {Object} hotspot     Individual hotspot config.
	 * @param {number} index       Position within parentLayer.hotspots.
	 * @return {string} Composite layer_id, e.g. `<uuid>::<sub-uuid>` or `<uuid>::idx0`.
	 */
	buildCompositeLayerId( parentLayer, hotspot, index ) {
		const parentId = parentLayer?.id ? String( parentLayer.id ) : '';
		if ( ! parentId ) {
			return '';
		}
		const subId = hotspot?.id ? String( hotspot.id ) : `idx${ index }`;
		return `${ parentId }::${ subId }`;
	}

	/**
	 * Emit a sub-hotspot interaction event into the sessionStorage buffer.
	 *
	 * Hotspots emit `viewed` (via emitLayerVisible when the layer becomes
	 * visible) as well as the engagement actions `hovered` and `clicked`.
	 * The layer also emits its own `viewed` via emitParentLayerEvent, which
	 * remains the layer-wide impression the backend aggregates for "All
	 * Hotspots (Cumulative)" by grouping on `parent_layer_id`.
	 *
	 * The per-hotspot `viewed` is what makes a hotspot's numbers correct when
	 * hotspots are added or removed over time: only an impression row recorded
	 * against the hotspot itself knows whether it existed when a viewer
	 * watched.
	 *
	 * All actions deduped per (composite layer_id, action_type, page-session)
	 * — one clicked, one hovered per sub-hotspot per session at most.
	 *
	 * Enriches every event with parent_layer_id, parent_layer_name,
	 * hotspot_index, dwell_ms, current_video_time, is_fullscreen,
	 * interaction_seq so the UI can group sub-hotspots by parent layer
	 * without parsing composite ids, and future analytics questions can
	 * be answered without re-instrumenting.
	 *
	 * @param {Object} parentLayer Parent layer config (has id, type, displayTime, name).
	 * @param {Object} hotspot     Individual hotspot config.
	 * @param {number} index       Hotspot's position in parentLayer.hotspots.
	 * @param {string} actionType  e.g. 'clicked', 'hovered'.
	 * @param {Object} [metadata]  Extra fields merged into layer_metadata.
	 * @param {Array}  [collector] When given, the built event is pushed here instead of written, so a caller can batch.
	 */
	emitHotspotEvent( parentLayer, hotspot, index, actionType, metadata, collector ) {
		// Build-only mode (a collector array is supplied) pushes the event for a
		// later batched write, so it does not need the immediate writer. Only the
		// direct-write path requires window.GoDAM.addLayerInteraction.
		if (
			! Array.isArray( collector ) &&
			( ! window.GoDAM ||
				typeof window.GoDAM.addLayerInteraction !== 'function' )
		) {
			return;
		}

		const videoKey = getVideoKey( this.player );
		if ( ! videoKey ) {
			return;
		}

		const compositeLayerId = this.buildCompositeLayerId( parentLayer, hotspot, index );
		if ( ! compositeLayerId ) {
			return;
		}

		// Per-(composite layer_id, action_type, session) dedupe.
		let firedActions = this._dedupeFired.get( compositeLayerId );
		if ( ! firedActions ) {
			firedActions = new Set();
			this._dedupeFired.set( compositeLayerId, firedActions );
		}
		if ( firedActions.has( actionType ) ) {
			return;
		}
		firedActions.add( actionType );

		// Dwell measures consideration time: the gap between parent visibility
		// and this hotspot interaction, minus time the tab was hidden during
		// that window. The per-hotspot `viewed` (emitted via emitLayerVisible)
		// does not seed a first-visible timestamp of its own, so dwell keys off
		// the parent's first-visible timestamp, seeded by emitParentLayerEvent
		// on parentLayer.id.
		const parentLayerId = parentLayer?.id ? String( parentLayer.id ) : '';
		let firstVisibleAt = this._firstVisibleAt.get( compositeLayerId );
		let hiddenAtStart = this._hiddenAtFirstVisible.get( compositeLayerId );
		if ( ! firstVisibleAt && parentLayerId ) {
			firstVisibleAt = this._firstVisibleAt.get( parentLayerId );
			hiddenAtStart = this._hiddenAtFirstVisible.get( parentLayerId );
		}
		let dwellMs = 0;
		if ( firstVisibleAt ) {
			const wallMs = Date.now() - firstVisibleAt;
			const hiddenNow = window.GoDAM?.getTabHiddenAccumulatedMs?.() || 0;
			dwellMs = Math.max( 0, wallMs - ( hiddenNow - ( hiddenAtStart || 0 ) ) );
		}

		const prevSeq = this._interactionSeq.get( compositeLayerId ) || 0;
		const seq = prevSeq + 1;
		this._interactionSeq.set( compositeLayerId, seq );

		let currentVideoTime = 0;
		try {
			currentVideoTime = Number( this.player?.currentTime?.() ) || 0;
		} catch ( e ) {
			currentVideoTime = 0;
		}

		let isFullscreen = false;
		try {
			isFullscreen = !! this.player?.isFullscreen?.();
		} catch ( e ) {
			isFullscreen = false;
		}

		// Device + viewer context for v1.5 slicing dimensions
		// (conversion-by-device, first-touch vs returning-viewer).
		const deviceType = window.GoDAM?.getDeviceType?.() || 'desktop';
		const wasFirstView = window.GoDAM?.wasFirstViewForVideo?.( videoKey ) || false;

		// `parent_layer_name` carries the resolved display name (custom if set,
		// otherwise `<TypeLabel> layer at <t>s`) so backend `argMax` aggregation
		// and the per-parent timeline marker have a non-UUID label even when
		// the marketer hasn't named the layer.
		const parentLayerName = getLayerDisplayName(
			parentLayer,
			parentLayer?.type || 'hotspot',
		);

		// Sub-hotspot label: "<parentName> — Hotspot N". Fallback to
		// "Hotspot N" alone if the parent name is missing (defensive — should
		// always be populated via getLayerDisplayName above).
		const subLabel = hotspot?.name || hotspot?.label || hotspot?.title || `Hotspot ${ index + 1 }`;
		const compositeName = parentLayerName ? `${ parentLayerName } — ${ subLabel }` : String( subLabel );

		const enrichedMetadata = {
			parent_layer_id: parentLayerId,
			parent_layer_name: parentLayerName,
			hotspot_index: index,
			hotspot_id: hotspot?.id ? String( hotspot.id ) : '',
			hotspot_link: hotspot?.link || '',
			dwell_ms: dwellMs,
			current_video_time: currentVideoTime,
			is_fullscreen: isFullscreen,
			interaction_seq: seq,
			device_type: deviceType,
			was_first_view: wasFirstView,
			...( metadata || {} ),
		};

		const event = {
			layer_id: compositeLayerId,
			layer_type: parentLayer?.type || 'hotspot',
			action_type: actionType,
			layer_timestamp: parseFloat( parentLayer?.displayTime ) || 0,
			layer_name: compositeName,
			page_url: window.location.href,
			layer_metadata: enrichedMetadata,
		};

		// When a collector array is supplied the caller is batching several
		// events into one storage write (see emitLayerVisible) — hand it over
		// instead of paying a full read/parse/stringify/write cycle here.
		if ( Array.isArray( collector ) ) {
			collector.push( event );
			return;
		}

		window.GoDAM.addLayerInteraction( videoKey, event );
	}

	/**
	 * Emit a parent-layer interaction event (no sub-hotspot identity).
	 *
	 * Used for events that belong to the layer as a whole rather than to a
	 * single sub-hotspot — currently just `viewed`, which fires once when
	 * the layer becomes visible. Engagement actions (hovered / clicked)
	 * stay per-sub-hotspot; the godam-analytics processing pipeline
	 * aggregates those by parent_layer_id for the "All Hotspots
	 * (Cumulative)" funnel.
	 *
	 * `layer_id` = `parentLayer.id` (no `::sub` suffix). Deduped per
	 * (layer_id, action_type, session) via the same _dedupeFired map that
	 * gates sub-hotspot events — so a viewer who briefly sees the layer
	 * twice within one pageload only emits one `viewed`. Also seeds
	 * `_firstVisibleAt` keyed on the parent's id, so sub-hotspot dwell
	 * measures from layer visibility rather than from first sub-event.
	 *
	 * @param {Object} parentLayer Parent layer config (.id required; .name / .displayTime / .type preferred).
	 * @param {string} actionType  e.g. 'viewed'.
	 * @param {Object} [metadata]  Extra fields merged into layer_metadata.
	 * @param {Array}  [collector] When given, the built event is pushed here instead of written, so a caller can batch.
	 */
	emitParentLayerEvent( parentLayer, actionType, metadata, collector ) {
		// Build-only mode (a collector array is supplied) only needs to push the
		// event; the immediate writer is required just for the direct-write path.
		if (
			! Array.isArray( collector ) &&
			( ! window.GoDAM ||
				typeof window.GoDAM.addLayerInteraction !== 'function' )
		) {
			return;
		}

		const videoKey = getVideoKey( this.player );
		if ( ! videoKey ) {
			return;
		}

		const parentLayerId = parentLayer?.id ? String( parentLayer.id ) : '';
		if ( ! parentLayerId ) {
			return;
		}

		// Shared dedupe map — parent's key (bare id) and sub-hotspots'
		// composite keys (parent::sub) can't collide.
		let firedActions = this._dedupeFired.get( parentLayerId );
		if ( ! firedActions ) {
			firedActions = new Set();
			this._dedupeFired.set( parentLayerId, firedActions );
		}
		if ( firedActions.has( actionType ) ) {
			return;
		}
		firedActions.add( actionType );

		const firstVisibleAt = this._firstVisibleAt.get( parentLayerId );
		let dwellMs = 0;
		if ( actionType === 'viewed' ) {
			this._firstVisibleAt.set( parentLayerId, Date.now() );
			this._hiddenAtFirstVisible.set(
				parentLayerId,
				window.GoDAM?.getTabHiddenAccumulatedMs?.() || 0,
			);
		} else if ( firstVisibleAt ) {
			const wallMs = Date.now() - firstVisibleAt;
			const hiddenAtStart = this._hiddenAtFirstVisible.get( parentLayerId ) || 0;
			const hiddenNow = window.GoDAM?.getTabHiddenAccumulatedMs?.() || 0;
			dwellMs = Math.max( 0, wallMs - ( hiddenNow - hiddenAtStart ) );
		}

		const prevSeq = this._interactionSeq.get( parentLayerId ) || 0;
		const seq = prevSeq + 1;
		this._interactionSeq.set( parentLayerId, seq );

		let currentVideoTime = 0;
		try {
			currentVideoTime = Number( this.player?.currentTime?.() ) || 0;
		} catch ( e ) {
			currentVideoTime = 0;
		}

		let isFullscreen = false;
		try {
			isFullscreen = !! this.player?.isFullscreen?.();
		} catch ( e ) {
			isFullscreen = false;
		}

		const deviceType = window.GoDAM?.getDeviceType?.() || 'desktop';
		const wasFirstView = window.GoDAM?.wasFirstViewForVideo?.( videoKey ) || false;
		const parentLayerName = getLayerDisplayName(
			parentLayer,
			parentLayer?.type || 'hotspot',
		);

		const enrichedMetadata = {
			parent_layer_id: parentLayerId,
			parent_layer_name: parentLayerName,
			dwell_ms: dwellMs,
			current_video_time: currentVideoTime,
			is_fullscreen: isFullscreen,
			interaction_seq: seq,
			device_type: deviceType,
			was_first_view: wasFirstView,
			...( metadata || {} ),
		};

		const event = {
			layer_id: parentLayerId,
			layer_type: parentLayer?.type || 'hotspot',
			action_type: actionType,
			layer_timestamp: parseFloat( parentLayer?.displayTime ) || 0,
			layer_name: parentLayerName,
			page_url: window.location.href,
			layer_metadata: enrichedMetadata,
		};

		if ( Array.isArray( collector ) ) {
			collector.push( event );
			return;
		}

		window.GoDAM.addLayerInteraction( videoKey, event );
	}

	/**
	 * Emit every impression event for a layer that has just become visible:
	 * the layer-wide `viewed` plus one `viewed` per hotspot.
	 *
	 * A per-hotspot `viewed` is NOT redundant with the layer's, even though all
	 * hotspots in a layer appear at the same instant. The *set* of hotspots
	 * changes as the video is edited, so a per-hotspot impression row is the
	 * only record of which hotspots actually existed when a viewer saw the
	 * layer. Reconstructing that afterwards from saved config dates cannot
	 * resolve an edit made the same day, because the analytics rollup buckets
	 * by day.
	 *
	 * All events go into one array and are written in a single storage
	 * operation, so an impression costs the same as it did when only the
	 * layer-level event fired.
	 *
	 * @param {Object} parentLayer Layer config (.id, .hotspots).
	 */
	emitLayerVisible( parentLayer ) {
		const videoKey = getVideoKey( this.player );
		if ( ! videoKey ) {
			return;
		}

		// A writer must exist before we build anything. emitParentLayerEvent /
		// emitHotspotEvent mark the per-session dedupe as they build (even in
		// collector mode), so building with no sink would burn the dedupe key
		// and the next visibility transition would emit nothing. Bailing here
		// leaves the dedupe untouched so a later transition still retries.
		const hasBatchWriter = typeof window.GoDAM?.addLayerInteractions === 'function';
		const hasSingleWriter = typeof window.GoDAM?.addLayerInteraction === 'function';
		if ( ! hasBatchWriter && ! hasSingleWriter ) {
			return;
		}

		const batch = [];
		this.emitParentLayerEvent( parentLayer, 'viewed', undefined, batch );

		const hotspots = Array.isArray( parentLayer?.hotspots )
			? parentLayer.hotspots
			: [];
		hotspots.forEach( ( hotspot, index ) => {
			// Skip the per-hotspot `viewed` for a hotspot with no stable id.
			// buildCompositeLayerId would fall back to a positional `idx<n>`
			// key, which re-attributes a deleted hotspot's history to whoever
			// takes its index — unacceptable now that `viewed` is the
			// conversion denominator. Such a hotspot keeps the layer-level
			// impression only (the pre-per-hotspot behaviour).
			if ( ! hotspot?.id ) {
				return;
			}
			this.emitHotspotEvent( parentLayer, hotspot, index, 'viewed', undefined, batch );
		} );

		if ( ! batch.length ) {
			return;
		}

		if ( hasBatchWriter ) {
			window.GoDAM.addLayerInteractions( videoKey, batch );
			return;
		}

		// Older bundle without the batch writer — still correct, just N writes.
		batch.forEach( ( event ) => window.GoDAM.addLayerInteraction( videoKey, event ) );
	}

	/**
	 * Setup hotspot layer
	 *
	 * @param {Object}      layer        - Layer configuration object
	 * @param {HTMLElement} layerElement - Layer DOM element
	 */
	setupHotspotLayer( layer, layerElement ) {
		const layerObj = {
			layerElement,
			displayTime: parseFloat( layer.displayTime ),
			duration: layer.duration ? parseInt( layer.duration ) : 0,
			show: true,
			hotspots: layer.hotspots || [],
			pauseOnHover: layer.pauseOnHover || false,
			// Stash the original config so emitLayerEvent has access to id/name/type.
			layer,
		};

		this.hotspotLayers.push( layerObj );
	}

	/**
	 * Handle hotspot layers time update
	 *
	 * @param {number} currentTime - Current video time in seconds
	 */
	handleHotspotLayersTimeUpdate( currentTime ) {
		const blockedByLayer = this.isDisplayingLayers?.[ this.currentPlayerVideoInstanceId ] === true;

		this.hotspotLayers.forEach( ( layerObj ) => {
			if ( ! layerObj.show ) {
				return;
			}

			const endTime = layerObj.displayTime + layerObj.duration;
			const isActive = currentTime >= layerObj.displayTime && currentTime < endTime;

			if ( blockedByLayer ) {
				if ( ! layerObj.layerElement.classList.contains( 'overlapped' ) ) {
					layerObj.layerElement.classList.add( 'overlapped' );
				}
			} else if ( layerObj.layerElement.classList.contains( 'overlapped' ) ) {
				layerObj.layerElement.classList.remove( 'overlapped' );
			}

			if ( isActive ) {
				if ( layerObj.layerElement.classList.contains( 'hidden' ) ) {
					layerObj.layerElement.classList.remove( 'hidden' );
					// One impression for the layer plus one per hotspot,
					// written in a single storage operation.
					this.emitLayerVisible( layerObj.layer );
					if ( ! layerObj.layerElement.dataset?.hotspotsInitialized ) {
						this.createHotspots( layerObj );
						layerObj.layerElement.dataset.hotspotsInitialized = true;
					} else {
						requestAnimationFrame( () => {
							const hotspotDivs = layerObj.layerElement.querySelectorAll( HOTSPOT_POINT_SELECTOR );
							hotspotDivs.forEach( ( hotspotDiv, index ) => {
								// Buttons size to their label and carry no tooltip. While the
								// layer was hidden they measured 0x0, so any resize skipped
								// their clamp; re-clamp them now that the layer is visible.
								if ( hotspotDiv.classList.contains( 'godam-hotspot-button' ) ) {
									this.positionHotspotButton( hotspotDiv, layerObj.hotspots[ index ] );
									return;
								}
								const tooltipDiv = hotspotDiv.querySelector( '.hotspot-tooltip' );
								if ( tooltipDiv ) {
									this.positionTooltip( hotspotDiv, tooltipDiv );
								}
							} );
						} );
					}
				}
			} else if ( ! layerObj.layerElement.classList.contains( 'hidden' ) ) {
				layerObj.layerElement.classList.add( 'hidden' );
			}
		} );
	}

	/**
	 * Handle fullscreen changes for hotspot layers
	 *
	 * @param {boolean}     isFullscreen   - Whether player is in fullscreen
	 * @param {HTMLElement} videoContainer - Video container element
	 */
	handleFullscreenChange( isFullscreen, videoContainer ) {
		this.hotspotLayers.forEach( ( layerObj ) => {
			if ( isFullscreen && ! videoContainer.contains( layerObj.layerElement ) ) {
				videoContainer.appendChild( layerObj.layerElement );
			}
		} );

		// Use requestAnimationFrame to wait for layout to stabilize after fullscreen resize
		let framesToWait = 2;
		const waitForResize = () => {
			if ( framesToWait > 0 ) {
				framesToWait--;
				window.requestAnimationFrame( waitForResize );
			} else {
				this.updateHotspotPositions();
			}
		};
		window.requestAnimationFrame( waitForResize );
	}

	/**
	 * Create hotspots for a layer
	 *
	 * @param {Object} layerObj - Layer object containing hotspots and configuration
	 */
	createHotspots( layerObj ) {
		const videoContainer = this.player.el();
		const containerWidth = videoContainer?.offsetWidth;
		const containerHeight = videoContainer?.offsetHeight;

		const baseWidth = HotspotLayerManager.BASE_WIDTH;
		const baseHeight = HotspotLayerManager.BASE_HEIGHT;

		layerObj.hotspots.forEach( ( hotspot, index ) => {
			const hotspotDiv = this.createHotspotElement( hotspot, index, containerWidth, containerHeight, baseWidth, baseHeight, layerObj.layer );

			this.setupHotspotHoverEvents( hotspotDiv, layerObj.pauseOnHover );

			// Track per-hotspot interactions. The layer event carries the
			// hotspot's index + link in `layer_metadata` so the analytics UI
			// can later attribute conversion per sub-hotspot if needed.
			this.setupHotspotAnalytics( hotspotDiv, layerObj, hotspot, index );

			layerObj.layerElement.appendChild( hotspotDiv );

			// A button can only be measured (and clamped inside the video) once it is
			// attached to the visible layer.
			if ( hotspotDiv.classList.contains( 'godam-hotspot-button' ) ) {
				this.positionHotspotButton( hotspotDiv, hotspot );
			}

			const tooltipDiv = hotspotDiv.querySelector( '.hotspot-tooltip' );
			if ( tooltipDiv ) {
				requestAnimationFrame( () => {
					this.positionTooltip( hotspotDiv, tooltipDiv );
				} );
			}
		} );
	}

	/**
	 * Attach click + hover analytics handlers to a single hotspot element.
	 *
	 * Both actions deduped per (composite layer_id, page-session) inside
	 * emitHotspotEvent — one click per sub-hotspot per session, one hover
	 * per sub-hotspot per session. Matches the analytical question "did the
	 * viewer click this hotspot at least once?" — repeat clicks within the
	 * same session aren't more meaningful for conversion-rate analysis.
	 *
	 * @param {HTMLElement} hotspotDiv The hotspot DOM element.
	 * @param {Object}      layerObj   The owning layer object.
	 * @param {Object}      hotspot    The hotspot config (link, text, index).
	 * @param {number}      index      The hotspot's index inside layerObj.hotspots.
	 */
	setupHotspotAnalytics( hotspotDiv, layerObj, hotspot, index ) {
		hotspotDiv.addEventListener( 'click', ( ev ) => {
			// Skip clicks on the tooltip-close affordance, which fires its own
			// handler and shouldn't be counted as a conversion click.
			if ( ev.target?.closest?.( '.hotspot-tooltip-close' ) ) {
				return;
			}
			// Touch devices fire `click`/tap without a preceding `mouseenter`,
			// so a tap would otherwise record `clicked` with no `hovered` —
			// making clicked > hovered and miscounting tappers in the funnel's
			// "No Action" bucket (No Action = viewed − hovered). Count the tap
			// as a hover first; the per-(composite layer_id, action_type,
			// session) dedupe in emitHotspotEvent makes it a no-op when a real
			// mouseenter already fired (desktop), so hovered ≥ clicked holds on
			// every device.
			this.emitHotspotEvent( layerObj.layer, hotspot, index, 'hovered' );
			this.emitHotspotEvent( layerObj.layer, hotspot, index, 'clicked' );
		} );

		hotspotDiv.addEventListener( 'mouseenter', () => {
			this.emitHotspotEvent( layerObj.layer, hotspot, index, 'hovered' );
		} );
	}

	/**
	 * Compute content rectangle
	 *
	 * @return {Object|null} Content rectangle {left, top, width, height} or null
	 */
	computeContentRect() {
		const videoEl = this.player.tech( true )?.el() || this.player.el().querySelector( 'video' );
		const containerEl = this.player.el();

		if ( ! videoEl || ! containerEl ) {
			return null;
		}

		const nativeW = videoEl.videoWidth || this.player.videoWidth() || 0;
		const nativeH = videoEl.videoHeight || this.player.videoHeight() || 0;

		// Measure the video's own box. It normally fills the player, but in
		// fullscreen with the transcript open the video (and the layers) are
		// confined to the area beside the panel, while the player still spans
		// the screen.
		const boxEl = videoEl.offsetWidth && videoEl.offsetHeight ? videoEl : containerEl;
		const elW = boxEl.offsetWidth;
		const elH = boxEl.offsetHeight;

		// If video dimensions aren't loaded yet, use full container
		if ( ! nativeW || ! nativeH ) {
			return {
				left: 0,
				top: 0,
				width: elW,
				height: elH,
			};
		}

		const videoAspectRatio = nativeW / nativeH;
		const containerAspectRatio = elW / elH;

		let contentW, contentH, offsetX, offsetY;

		if ( containerAspectRatio > videoAspectRatio ) {
			// Pillarboxed (black bars on left/right)
			contentH = elH;
			contentW = elH * videoAspectRatio;
			offsetX = ( elW - contentW ) / 2;
			offsetY = 0;
		} else {
			// Letterboxed (black bars on top/bottom)
			contentW = elW;
			contentH = elW / videoAspectRatio;
			offsetX = 0;
			offsetY = ( elH - contentH ) / 2;
		}

		const result = {
			left: Math.round( offsetX ),
			top: Math.round( offsetY ),
			width: Math.round( contentW ),
			height: Math.round( contentH ),
		};

		return result;
	}

	/**
	 * Create hotspot element
	 *
	 * @param {Object} hotspot         - Hotspot configuration object
	 * @param {number} index           - Index of the hotspot
	 * @param {number} containerWidth  - Width of the video container
	 * @param {number} containerHeight - Height of the video container
	 * @param {number} baseWidth       - Base width for calculations
	 * @param {number} baseHeight      - Base height for calculations
	 * @param {Object} [layer]         - Parent layer config (for shared style resolution)
	 * @return {HTMLElement} Created hotspot element
	 */
	createHotspotElement( hotspot, index, containerWidth, containerHeight, baseWidth, baseHeight, layer ) {
		// Button style: the point is a button, not a circle.
		if ( isButtonHotspot( layer, hotspot ) ) {
			return this.createHotspotButtonElement( hotspot, index );
		}

		const hotspotDiv = document.createElement( 'div' );
		hotspotDiv.classList.add( 'hotspot', 'circle' );
		hotspotDiv.style.position = 'absolute';

		const contentRect = this.computeContentRect();

		// Positioning
		const fallbackPosX = hotspot.oPosition?.x ?? hotspot.position.x;
		const fallbackPosY = hotspot.oPosition?.y ?? hotspot.position.y;

		let fallbackDiameter = hotspot.oSize?.diameter ?? hotspot.size?.diameter;
		if ( ! fallbackDiameter ) {
			if ( hotspot.unit === 'percent' && contentRect ) {
				fallbackDiameter = ( HOTSPOT_CONSTANTS.DEFAULT_DIAMETER_PX / contentRect.width ) * 100;
			} else {
				fallbackDiameter = hotspot.unit === 'percent' ? HOTSPOT_CONSTANTS.DEFAULT_DIAMETER_PERCENT : HOTSPOT_CONSTANTS.DEFAULT_DIAMETER_PX;
			}
		}

		let pixelX, pixelY, pixelDiameter;

		if ( hotspot.unit === 'percent' && contentRect ) {
			// New percentage-based positioning
			pixelX = contentRect.left + ( ( fallbackPosX / 100 ) * contentRect.width );
			pixelY = contentRect.top + ( ( fallbackPosY / 100 ) * contentRect.height );
			pixelDiameter = ( fallbackDiameter / 100 ) * contentRect.width;
		} else {
			// Legacy pixel-based positioning (relative to 800x600)
			// We now map these to the contentRect instead of the full container to avoid black bars
			const effectiveRect = contentRect || { left: 0, top: 0, width: containerWidth, height: containerHeight };
			pixelX = effectiveRect.left + ( ( fallbackPosX / baseWidth ) * effectiveRect.width );
			pixelY = effectiveRect.top + ( ( fallbackPosY / baseHeight ) * effectiveRect.height );
			pixelDiameter = ( fallbackDiameter / baseWidth ) * effectiveRect.width;
		}

		hotspotDiv.style.left = `${ pixelX }px`;
		hotspotDiv.style.top = `${ pixelY }px`;
		hotspotDiv.style.width = `${ pixelDiameter }px`;
		hotspotDiv.style.height = `${ pixelDiameter }px`;

		// Resolve effective style — shared at the layer level for new layers,
		// per-hotspot for legacy layers (backward compatibility).
		const style = resolveHotspotStyle( layer, hotspot );

		// Background color — a single resolved circle fill (pulse colour in pulse
		// mode, background colour in icon mode), exactly like the Woo hotspot.
		hotspotDiv.style.backgroundColor = style.color || '#0c80dfa6';

		// Create content
		const hotspotContent = this.createHotspotContent( hotspot, index, hotspotDiv, style );
		hotspotDiv.appendChild( hotspotContent );

		return hotspotDiv;
	}

	/**
	 * Create a button-style hotspot point (the layer's "Button" style).
	 *
	 * Renders the hotspot's Button style: text or die-cut sticker variant,
	 * per-hotspot colours and attention animation (all from `_button-cta.scss`).
	 * The label is the hotspot's tooltip text, there is no hover tooltip, and the
	 * button itself opens the hotspot link. It deliberately does NOT carry the
	 * `.hotspot` / `.circle` classes, whose circle styles would override it.
	 *
	 * @param {Object} hotspot Hotspot configuration object.
	 * @param {number} index   Index of the hotspot.
	 * @return {HTMLElement} The created anchor element.
	 */
	createHotspotButtonElement( hotspot, index ) {
		const style = resolveButtonCtaStyle( hotspot );

		const buttonEl = document.createElement( 'a' );
		buttonEl.className = getButtonCtaClassName( style, { extra: 'godam-hotspot-button' } );
		// Stable hooks for e2e: the rendered button, its index and its variant.
		// Class names here are style-dependent, so they cannot be selected on.
		buttonEl.setAttribute( 'data-test-id', `godam-hotspot-render-button-${ index }` );
		buttonEl.setAttribute( 'data-button-variant', style.variant );

		/* translators: %d: hotspot number */
		const label = hotspot.tooltipText || sprintf( __( 'Hotspot %d', 'godam' ), index + 1 );
		// textContent (never innerHTML) keeps author-entered labels inert.
		buttonEl.textContent = label;
		// The sticker variant draws its die-cut border from `data-sticker-text`.
		if ( style.variant === 'sticker' ) {
			buttonEl.setAttribute( 'data-sticker-text', label );
		}

		if ( hotspot.link && isSafeLinkUrl( hotspot.link ) ) {
			buttonEl.href = hotspot.link;
			buttonEl.target = '_blank';
			buttonEl.rel = 'noopener noreferrer';
		}

		buttonEl.style.position = 'absolute';
		Object.entries( getButtonCtaCssVars( style ) ).forEach( ( [ prop, value ] ) => {
			buttonEl.style.setProperty( prop, value );
		} );

		this.positionHotspotButton( buttonEl, hotspot );

		return buttonEl;
	}

	/**
	 * Place a button-style hotspot with its top-left corner on the hotspot's
	 * position, inside the video content box (same anchoring as the circles).
	 * Buttons size to their label, so only left/top are set — then clamped so the
	 * button stays inside the content box: the saved position scales with the video
	 * but the button's pixel size doesn't, so a point near the bottom or right edge
	 * would otherwise overflow (and be clipped by) a small player.
	 *
	 * @param {HTMLElement} buttonEl The button element.
	 * @param {Object}      hotspot  Hotspot configuration object.
	 */
	positionHotspotButton( buttonEl, hotspot ) {
		const containerEl = this.player.el();
		const rect = this.computeContentRect() || {
			left: 0,
			top: 0,
			width: containerEl?.offsetWidth || 0,
			height: containerEl?.offsetHeight || 0,
		};

		const posX = hotspot.oPosition?.x ?? hotspot.position?.x ?? 50;
		const posY = hotspot.oPosition?.y ?? hotspot.position?.y ?? 50;
		// Percent units for current hotspots; legacy pixel units are relative to 800x600.
		const isPercent = hotspot.unit === 'percent';
		const fractionX = isPercent ? posX / 100 : posX / HotspotLayerManager.BASE_WIDTH;
		const fractionY = isPercent ? posY / 100 : posY / HotspotLayerManager.BASE_HEIGHT;

		// Size: a button's font-size is a percentage of the content width, so it
		// scales with the video (like the circle hotspots' diameter). Every button
		// resolves a percent — its stored `fontPercent` or the per-variant default —
		// so it always scales, never a fixed px size. Set it before measuring below
		// so the edge-clamp uses the scaled size.
		const fontPx = getButtonFontPx( resolveButtonFontPercent( hotspot ), rect.width );
		if ( fontPx !== null ) {
			buttonEl.style.fontSize = `${ fontPx }px`;
		}

		let left = rect.left + ( fractionX * rect.width );
		let top = rect.top + ( fractionY * rect.height );
		buttonEl.style.left = `${ left }px`;
		buttonEl.style.top = `${ top }px`;

		// Measure after placing it (its wrapped width depends on where it sits). A
		// button that isn't laid out yet (hidden layer, not attached) measures 0 and
		// is clamped on the next reposition instead.
		let width = buttonEl.offsetWidth;
		let height = buttonEl.offsetHeight;

		// Overall-size cap: shrink the font so the button fits within 90% of the
		// video width (small players, long labels, or an author-oversized button).
		// The sticker is a single nowrap line and can't wrap, so without this it
		// would spill off a narrow screen.
		if ( fontPx !== null && width ) {
			const fitted = fitButtonFontPx( fontPx, width, rect.width );
			if ( fitted !== fontPx ) {
				buttonEl.style.fontSize = `${ fitted }px`;
				width = buttonEl.offsetWidth;
				height = buttonEl.offsetHeight;
			}
		}

		if ( width && height ) {
			left = Math.max( rect.left, Math.min( left, rect.left + rect.width - width ) );
			top = Math.max( rect.top, Math.min( top, rect.top + rect.height - height ) );
			buttonEl.style.left = `${ left }px`;
			buttonEl.style.top = `${ top }px`;
		}
	}

	/**
	 * Create hotspot content
	 *
	 * @param {Object}      hotspot    - Hotspot configuration object
	 * @param {number}      index      - Index of the hotspot
	 * @param {HTMLElement} hotspotDiv - Parent hotspot div element
	 * @param {Object}      [style]    - Resolved style { icon, customIconUrl, color, iconColor, backgroundColor }
	 * @return {HTMLElement} Created content element
	 */
	createHotspotContent( hotspot, index, hotspotDiv, style ) {
		const effective = style || resolveHotspotStyle( null, hotspot );
		const hotspotContent = document.createElement( 'div' );
		hotspotContent.classList.add( 'hotspot-content' );
		hotspotContent.style.position = 'relative';
		hotspotContent.style.width = '100%';
		hotspotContent.style.height = '100%';

		if ( effective.icon ) {
			const iconEl = this.createHotspotIcon( effective.icon, effective.iconColor );
			hotspotContent.appendChild( iconEl );
		} else if ( effective.customIconUrl ) {
			const customIconEl = this.createCustomIcon( effective.customIconUrl, effective.color, hotspotDiv );
			hotspotContent.appendChild( customIconEl );
		} else {
			hotspotContent.classList.add( 'no-icon' );
		}

		const tooltipDiv = this.createHotspotTooltip( hotspot, index );
		hotspotContent.appendChild( tooltipDiv );

		return hotspotContent;
	}

	/**
	 * Create hotspot icon
	 *
	 * @param {string} icon        - Icon configuration or path
	 * @param {string} [iconColor] - Glyph colour (defaults to white)
	 * @return {HTMLElement} Created icon element
	 */
	createHotspotIcon( icon, iconColor = '#ffffff' ) {
		const iconEl = document.createElement( 'i' );
		iconEl.className = `fa-solid fa-${ icon }`;
		iconEl.style.width = '50%';
		iconEl.style.height = '50%';
		iconEl.style.fontSize = '1.6em';
		iconEl.style.display = 'flex';
		iconEl.style.alignItems = 'center';
		iconEl.style.justifyContent = 'center';
		iconEl.style.margin = 'auto';
		iconEl.style.color = iconColor;

		return iconEl;
	}

	/**
	 * Create custom icon element
	 *
	 * @param {string}      customIconUrl   - URL of the custom icon
	 * @param {string}      backgroundColor - Background color for fallback
	 * @param {HTMLElement} hotspotDiv      - Parent hotspot div element
	 * @return {HTMLElement} Created custom icon element
	 */
	createCustomIcon( customIconUrl, backgroundColor, hotspotDiv ) {
		const customIconEl = document.createElement( 'img' );
		customIconEl.src = customIconUrl;
		customIconEl.alt = __( 'Custom Icon', 'godam' );
		customIconEl.style.width = '50%';
		customIconEl.style.height = '50%';
		customIconEl.style.maxWidth = '100%';
		customIconEl.style.maxHeight = '100%';
		customIconEl.style.objectFit = 'contain';
		customIconEl.style.display = 'block';
		customIconEl.style.margin = 'auto';
		customIconEl.style.pointerEvents = 'none';

		// Add error handling for failed image loads - convert to normal hotspot point
		customIconEl.onerror = function() {
			// Remove the failed image element
			customIconEl.remove();

			// Get the hotspot content container
			const hotspotContent = hotspotDiv?.querySelector( '.hotspot-content' );
			if ( hotspotContent ) {
				// Add no-icon class to show as normal hotspot
				hotspotContent.classList.add( 'no-icon' );
			}

			// Reset hotspot background to default (non-icon) color
			if ( hotspotDiv ) {
				hotspotDiv.style.backgroundColor = backgroundColor || '#0c80dfa6';
			}
		};

		return customIconEl;
	}

	/**
	 * Create hotspot tooltip
	 *
	 * @param {Object} hotspot - Hotspot configuration object
	 * @param {number} index   - Index of the hotspot
	 * @return {HTMLElement} Created tooltip element
	 */
	createHotspotTooltip( hotspot, index ) {
		const tooltipDiv = document.createElement( 'div' );
		tooltipDiv.classList.add( 'hotspot-tooltip' );
		/* translators: %d: hotspot number */
		tooltipDiv.textContent = hotspot.tooltipText || sprintf( __( 'Hotspot %d', 'godam' ), index + 1 );

		if ( hotspot.link ) {
			const hotspotLink = document.createElement( 'a' );
			hotspotLink.href = hotspot.link;
			hotspotLink.target = '_blank';
			/* translators: %d: hotspot number */
			hotspotLink.textContent = hotspot.tooltipText || sprintf( __( 'Hotspot %d', 'godam' ), index + 1 );
			tooltipDiv.textContent = '';
			tooltipDiv.appendChild( hotspotLink );
		}

		return tooltipDiv;
	}

	/**
	 * Setup hotspot hover events
	 *
	 * @param {HTMLElement} hotspotDiv   - The hotspot element to add hover events to
	 * @param {boolean}     pauseOnHover - Whether to pause video playback on hover
	 */
	setupHotspotHoverEvents( hotspotDiv, pauseOnHover = false ) {
		hotspotDiv.addEventListener( 'mouseenter', () => {
			if ( pauseOnHover ) {
				this.wasPlayingBeforeHover = ! this.player.paused();
				this.player.pause();
			}

			const layer = hotspotDiv.closest( '.easydam-layer.hotspot-layer' );

			if ( layer ) {
				const computedZ = window.getComputedStyle( layer ).zIndex;
				const current = parseInt( computedZ, 10 ) || 0;
				layer.style.zIndex = current + 1;
			}
		} );

		hotspotDiv.addEventListener( 'mouseleave', () => {
			if ( pauseOnHover && this.wasPlayingBeforeHover ) {
				this.player.play();
			}

			const layer = hotspotDiv.closest( '.easydam-layer.hotspot-layer' );
			if ( layer ) {
				layer.style.zIndex = '';
			}
		} );
	}

	/**
	 * Position tooltip relative to hotspot, constrained within the video container
	 *
	 * @param {HTMLElement} hotspotDiv - The hotspot element for positioning reference
	 * @param {HTMLElement} tooltipDiv - The tooltip element to position
	 */
	positionTooltip( hotspotDiv, tooltipDiv ) {
		const videoContainer = this.player.el();
		const containerRect = videoContainer.getBoundingClientRect();
		const hotspotRect = hotspotDiv.getBoundingClientRect();

		// Temporarily make tooltip visible to measure it accurately
		const originalVisibility = tooltipDiv.style.visibility;
		const originalOpacity = tooltipDiv.style.opacity;
		tooltipDiv.style.visibility = 'hidden';
		tooltipDiv.style.opacity = '0';
		tooltipDiv.style.display = 'block';

		const tooltipRect = tooltipDiv.getBoundingClientRect();

		// Restore original styles
		tooltipDiv.style.visibility = originalVisibility;
		tooltipDiv.style.opacity = originalOpacity;

		// Calculate space relative to video container (not viewport)
		const spaceAbove = hotspotRect.top - containerRect.top;
		const spaceBelow = containerRect.bottom - hotspotRect.bottom;

		const tooltipHeight = tooltipRect.height;
		const tooltipWidth = tooltipRect.width;

		// Minimum padding from container edges
		const edgePadding = 8;

		// Reset all positioning classes and styles first
		tooltipDiv.classList.remove( 'tooltip-top', 'tooltip-bottom', 'tooltip-left', 'tooltip-right', 'no-arrow' );
		tooltipDiv.style.top = '';
		tooltipDiv.style.bottom = '';
		tooltipDiv.style.left = '';
		tooltipDiv.style.right = '';
		tooltipDiv.style.transform = '';

		// Vertical positioning - prefer above, fallback to below
		if ( spaceAbove >= tooltipHeight + edgePadding ) {
			// Place above
			tooltipDiv.style.bottom = '100%';
			tooltipDiv.style.top = 'auto';
			tooltipDiv.classList.add( 'tooltip-top' );
		} else if ( spaceBelow >= tooltipHeight + edgePadding ) {
			// Place below
			tooltipDiv.style.bottom = 'auto';
			tooltipDiv.style.top = '100%';
			tooltipDiv.classList.add( 'tooltip-bottom' );
		} else if ( spaceAbove >= spaceBelow ) {
			tooltipDiv.style.bottom = '100%';
			tooltipDiv.style.top = 'auto';
			tooltipDiv.classList.add( 'tooltip-top' );
		} else {
			tooltipDiv.style.bottom = 'auto';
			tooltipDiv.style.top = '100%';
			tooltipDiv.classList.add( 'tooltip-bottom' );
		}

		// Horizontal positioning - calculate where tooltip would overflow
		const hotspotCenterInContainer = ( hotspotRect.left + ( hotspotRect.width / 2 ) ) - containerRect.left;
		const tooltipHalfWidth = tooltipWidth / 2;

		// Check if centered tooltip would overflow left or right of container
		const wouldOverflowLeft = ( hotspotCenterInContainer - tooltipHalfWidth ) < edgePadding;
		const wouldOverflowRight = ( hotspotCenterInContainer + tooltipHalfWidth ) > ( containerRect.width - edgePadding );

		if ( wouldOverflowLeft && wouldOverflowRight ) {
			// Tooltip is wider than available space, center it as best as possible
			tooltipDiv.style.left = '50%';
			tooltipDiv.style.transform = 'translateX(-50%)';
			tooltipDiv.classList.add( 'no-arrow' );
		} else if ( wouldOverflowLeft ) {
			// Align tooltip to the left edge of hotspot, but ensure it stays within container
			const leftOffset = Math.max( edgePadding - ( hotspotRect.left - containerRect.left ), 0 );
			tooltipDiv.style.left = `${ leftOffset }px`;
			tooltipDiv.style.right = 'auto';
			tooltipDiv.style.transform = 'translateX(0)';
			tooltipDiv.classList.add( 'tooltip-left', 'no-arrow' );
		} else if ( wouldOverflowRight ) {
			// Align tooltip to the right edge of hotspot, but ensure it stays within container
			const rightOffset = Math.max( edgePadding - ( containerRect.right - hotspotRect.right ), 0 );
			tooltipDiv.style.left = 'auto';
			tooltipDiv.style.right = `${ rightOffset }px`;
			tooltipDiv.style.transform = 'translateX(0)';
			tooltipDiv.classList.add( 'tooltip-right', 'no-arrow' );
		} else {
			// Centered horizontally - tooltip fits within container
			tooltipDiv.style.left = '50%';
			tooltipDiv.style.right = 'auto';
			tooltipDiv.style.transform = 'translateX(-50%)';
		}
	}

	/**
	 * Update hotspot positions on resize
	 */
	updateHotspotPositions() {
		const videoContainer = this.player.el();
		const containerWidth = videoContainer?.offsetWidth;
		const containerHeight = videoContainer?.offsetHeight;

		const baseWidth = HotspotLayerManager.BASE_WIDTH;
		const baseHeight = HotspotLayerManager.BASE_HEIGHT;

		const contentRect = this.computeContentRect();

		this.hotspotLayers.forEach( ( layerObj ) => {
			const isLayerHidden = layerObj.layerElement.classList.contains( 'hidden' );
			const hotspotDivs = layerObj.layerElement.querySelectorAll( HOTSPOT_POINT_SELECTOR );
			hotspotDivs.forEach( ( hotspotDiv, index ) => {
				const hotspot = layerObj.hotspots[ index ];

				// Buttons size to their label and have no tooltip — only move them.
				if ( hotspotDiv.classList.contains( 'godam-hotspot-button' ) ) {
					this.positionHotspotButton( hotspotDiv, hotspot );
					return;
				}

				// Recalc position
				const fallbackPosX = hotspot.oPosition?.x ?? hotspot.position.x;
				const fallbackPosY = hotspot.oPosition?.y ?? hotspot.position.y;
				const fallbackDiameter = hotspot.oSize?.diameter ?? hotspot.size?.diameter ?? 48;

				let pixelX, pixelY, pixelDiameter;

				if ( hotspot.unit === 'percent' && contentRect ) {
					// New percentage-based positioning
					pixelX = contentRect.left + ( ( fallbackPosX / 100 ) * contentRect.width );
					pixelY = contentRect.top + ( ( fallbackPosY / 100 ) * contentRect.height );
					pixelDiameter = ( fallbackDiameter / 100 ) * contentRect.width;
				} else {
					// Legacy pixel-based positioning
					// We now map these to the contentRect instead of the full container to avoid black bars
					const effectiveRect = contentRect || { left: 0, top: 0, width: containerWidth, height: containerHeight };
					pixelX = effectiveRect.left + ( ( fallbackPosX / baseWidth ) * effectiveRect.width );
					pixelY = effectiveRect.top + ( ( fallbackPosY / baseHeight ) * effectiveRect.height );
					pixelDiameter = ( fallbackDiameter / baseWidth ) * effectiveRect.width;
				}

				hotspotDiv.style.left = `${ pixelX }px`;
				hotspotDiv.style.top = `${ pixelY }px`;
				hotspotDiv.style.width = `${ pixelDiameter }px`;
				hotspotDiv.style.height = `${ pixelDiameter }px`;

				// Tooltip geometry relies on measurable layout; skip while layer is hidden.
				if ( isLayerHidden ) {
					return;
				}

				const tooltipDiv = hotspotDiv.querySelector( '.hotspot-tooltip' );
				if ( tooltipDiv ) {
					requestAnimationFrame( () => {
						this.positionTooltip( hotspotDiv, tooltipDiv );
					} );
				}
			} );
		} );
	}

	/**
	 * Reset hotspot layer state
	 */
	reset() {
		this.hotspotLayers = [];
		this.wasPlayingBeforeHover = false;
	}
}
